import { getActiveBrandId } from "@eweadn-qiankun/shared-utils";

/** 与 shared-theme 中 ThemeColorMode 一致 */
type ThemeColorMode = "light" | "dark";

type BrandId = "eweadn" | "exx";

/** 各品牌默认主题色（浅色 / 深色相互隔离） */
const BRAND_DEFAULT_PRIMARY: Record<BrandId, Record<ThemeColorMode, string>> = {
  eweadn: {
    light: "#1E2023",
    dark: "#FFFFFFF5",
  },
  exx: {
    // 与 brand-config/brands/exx/theme.json primaryColor 对齐
    light: "#F30000",
    // EXX 未配置 primaryColorDark，深色回退到全局默认浅色底
    dark: "#FFFFFFF5",
  },
};

const LEGACY_CUSTOM_PRIMARY_STORAGE_KEY = "app-custom-primary-colors";

export type CustomPrimaryColors = Partial<Record<ThemeColorMode, string>>;

/** 归一化为主题色模块使用的品牌 id */
const resolveBrandId = (): BrandId =>
  getActiveBrandId() === "exx" ? "exx" : "eweadn";

/** 按品牌读取默认主题色 */
export const getDefaultPrimaryByMode = (mode: ThemeColorMode): string =>
  BRAND_DEFAULT_PRIMARY[resolveBrandId()][mode];

/**
 * @deprecated 请使用 getDefaultPrimaryByMode；保留仅为兼容旧引用
 * 注意：该对象在模块加载时固定，切换品牌构建请改用 getDefaultPrimaryByMode
 */
export const DEFAULT_PRIMARY_BY_MODE: Record<ThemeColorMode, string> =
  BRAND_DEFAULT_PRIMARY.eweadn;

/** 按品牌隔离的自定义主题色存储 key，避免 eweadn / exx 互相覆盖 */
export const getCustomPrimaryStorageKey = () =>
  `${LEGACY_CUSTOM_PRIMARY_STORAGE_KEY}:${resolveBrandId()}`;

/** @deprecated 请使用 getCustomPrimaryStorageKey */
export const CUSTOM_PRIMARY_STORAGE_KEY = LEGACY_CUSTOM_PRIMARY_STORAGE_KEY;

/** 归一化 HEX，支持 #RRGGBB / #RRGGBBAA */
export const normalizeHex = (color: string) => {
  const value = color.trim();
  if (!value) return "";
  const withHash = value.startsWith("#") ? value : `#${value}`;
  if (!/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(withHash)) return "";
  return withHash.toUpperCase();
};

/** ColorPicker 仅支持不透明 #RRGGBB */
export const toOpaqueHex = (color: string) => {
  const normalized = normalizeHex(color);
  if (!normalized) return "#000000";
  return normalized.slice(0, 7);
};

const parseHexRgb = (hex: string) => {
  const opaque = toOpaqueHex(hex);
  return {
    r: Number.parseInt(opaque.slice(1, 3), 16),
    g: Number.parseInt(opaque.slice(3, 5), 16),
    b: Number.parseInt(opaque.slice(5, 7), 16),
  };
};

export const hexToRgba = (hex: string, alpha: number) => {
  const { r, g, b } = parseHexRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const isSameHexColor = (a: string, b: string) =>
  normalizeHex(a) === normalizeHex(b);

export const isDefaultPrimaryColor = (mode: ThemeColorMode, color: string) =>
  isSameHexColor(color, getDefaultPrimaryByMode(mode));

const parseStoredCustomPrimaryColors = (
  raw: string | null
): CustomPrimaryColors => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as CustomPrimaryColors;
    return {
      light: parsed.light ? normalizeHex(parsed.light) || undefined : undefined,
      dark: parsed.dark ? normalizeHex(parsed.dark) || undefined : undefined,
    };
  } catch {
    return {};
  }
};

export const readStoredCustomPrimaryColors = (): CustomPrimaryColors => {
  const brandKey = getCustomPrimaryStorageKey();
  const branded = parseStoredCustomPrimaryColors(
    localStorage.getItem(brandKey)
  );
  if (branded.light || branded.dark) return branded;

  // 兼容旧版未按品牌隔离的存储（仅 eweadn 迁移）
  if (resolveBrandId() === "eweadn") {
    return parseStoredCustomPrimaryColors(
      localStorage.getItem(LEGACY_CUSTOM_PRIMARY_STORAGE_KEY)
    );
  }

  return {};
};

export const writeStoredCustomPrimaryColor = (
  mode: ThemeColorMode,
  color: string | null
) => {
  const next = readStoredCustomPrimaryColors();
  if (color) {
    next[mode] = color;
  } else {
    delete next[mode];
  }
  localStorage.setItem(getCustomPrimaryStorageKey(), JSON.stringify(next));
};

/** 收集需要同步主题色的 DOM 节点（document + 当前 qiankun 子应用根） */
export const resolveThemeColorTargets = (
  element?: HTMLElement
): HTMLElement[] => {
  const targets = new Set<HTMLElement>();
  targets.add(element || document.documentElement);
  targets.add(document.documentElement);

  if (typeof window !== "undefined") {
    const portalRoot = (
      window as Window & { __EWEADN_QIANKUN_PORTAL_ROOT__?: HTMLElement }
    ).__EWEADN_QIANKUN_PORTAL_ROOT__;
    if (portalRoot) targets.add(portalRoot);
  }

  return Array.from(targets);
};

/** 主题色透明度阶梯（85 → 04），与 --bg-* / --text-* 对齐 */
export const PRIMARY_OPACITY_STEPS = [
  { suffix: "85", alpha: 0.85 },
  { suffix: "65", alpha: 0.65 },
  { suffix: "45", alpha: 0.45 },
  { suffix: "25", alpha: 0.25 },
  { suffix: "15", alpha: 0.15 },
  { suffix: "09", alpha: 0.09 },
  { suffix: "04", alpha: 0.04 },
] as const;

const writePrimaryColorVars = (
  target: HTMLElement,
  mode: ThemeColorMode,
  color: string
) => {
  target.style.setProperty(`--custom-primary-color-${mode}`, color);
  target.style.setProperty(
    `--custom-primary-color-${mode}-hover`,
    hexToRgba(color, 0.85)
  );
  PRIMARY_OPACITY_STEPS.forEach(({ suffix, alpha }) => {
    target.style.setProperty(
      `--custom-primary-${suffix}-${mode}`,
      hexToRgba(color, alpha)
    );
  });
};

export const clearCustomPrimaryCssVars = (
  mode: ThemeColorMode,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    target.style.removeProperty(`--custom-primary-color-${mode}`);
    target.style.removeProperty(`--custom-primary-color-${mode}-hover`);
    PRIMARY_OPACITY_STEPS.forEach(({ suffix }) => {
      target.style.removeProperty(`--custom-primary-${suffix}-${mode}`);
    });
  });
};

export const applyPrimaryColor = (
  mode: ThemeColorMode,
  color: string,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    writePrimaryColorVars(target, mode, color);
  });
};

/** 读取某模式下已生效的主题色（优先 localStorage） */
export const readAppliedPrimaryColor = (mode: ThemeColorMode) => {
  const stored = readStoredCustomPrimaryColors()[mode];
  if (stored) return stored;
  return getDefaultPrimaryByMode(mode);
};

/**
 * 将 localStorage 中的自定义主题色恢复到 DOM。
 * 浅色 / 深色分别写入，互不影响。
 * 子应用在 initBrandTheme 之后必须调用，否则会被品牌默认色覆盖。
 */
export const restoreStoredCustomPrimaryColors = (element?: HTMLElement) => {
  const stored = readStoredCustomPrimaryColors();
  resolveThemeColorTargets(element).forEach((target) => {
    (["light", "dark"] as const).forEach((mode) => {
      const color = stored[mode];
      if (color) {
        writePrimaryColorVars(target, mode, color);
      }
    });
  });
};
